/* eslint-disable complexity */
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'redux-first-history';
import { isNumber } from 'lodash';

import Frap from 'shared/app/components/frap-with-messages';
import FrapContainer from 'shared/app/components/frap-container';
import {
  signedInSelector,
  hasGuestSessionSelector,
  isCreatingGuestSessionSelector,
} from 'shared/app/bundles/user';
import { getFraudReputation } from 'shared/app/utils/iovation';
import { createGuestSession } from 'shared/app/state/action-creators/guest';

import FavoriteButtonIcon from '../../shared/favorite-button-icon';
import { usePdpContext } from '../pdp-provider';
import useInAppOrderTypeModal from '../../in-app-order-type-modal/use-in-app-order-type-modal';

import messages from './messages';
import styles from '../styles.cssm';

export const ProductDetailsFraps = () => {
  const { formatMessage, formatNumber, formats } = useIntl();
  const isSignedIn = useSelector(signedInSelector);
  const hasGuestSession = useSelector(hasGuestSessionSelector);
  const isCreatingGuestSession = useSelector(isCreatingGuestSessionSelector);
  const dispatch = useDispatch();
  const { showOrderTypeModal } = useInAppOrderTypeModal();

  const {
    isDemystifyMenuPdpEnabled,
    handleAddToCartClick,
    handleBackToCartClick,
    handleEditCartItemClick,
    handleFavoriteClick,
    isComingFromCart,
    isComingFromFav,
    isComingFromPrev,
    isFavorite,
    product,
    sizeInfo,
  } = usePdpContext();

  if (product?.error || product?.loading) {
    // we need the default product size before an order
    // can be placed
    return null;
  }

  const onAddToCartClickHandler = async (e) => {
    if (!isSignedIn && !hasGuestSession && !isCreatingGuestSession) {
      const reputation = await getFraudReputation();
      dispatch(createGuestSession({ reputation }));
    }
    handleAddToCartClick(e);
    showOrderTypeModal();
  };

  const favOrPrevDataMessage = isComingFromFav
    ? 'back-to-favorites-button'
    : 'back-to-previous-button';
  const handleBackToPrevOrFavClick = () => {
    isComingFromFav
      ? dispatch(push(`/menu/favorites`))
      : dispatch(push(`/menu/previous`));
  };

  const showTotalAmount =
    isDemystifyMenuPdpEnabled && isNumber(sizeInfo.selectedSizeTotalPrice);
  const totalAmount = showTotalAmount
    ? formatNumber(sizeInfo.selectedSizeTotalPrice, formats.number.money)
    : '';

  return (
    <FrapContainer className={styles.frap}>
      <div className="flex flex-column items-end">
        {isComingFromCart ? (
          <React.Fragment>
            <Frap
              className="mb3"
              data-e2e="back-to-order-button"
              mini
              onClick={handleBackToCartClick}
            >
              {formatMessage(messages.backToOrder)}
            </Frap>
            <Frap
              data-e2e="update-item-button"
              onClick={handleEditCartItemClick}
            >
              {formatMessage(messages.updateItem, {
                totalAmount,
              })}
            </Frap>
          </React.Fragment>
        ) : (
          <React.Fragment>
            {isSignedIn && !isComingFromFav && !isComingFromPrev && (
              <Frap
                circular
                className="mb3"
                data-e2e="favorite-product-button"
                mini
                onClick={() => {
                  handleFavoriteClick();
                }}
              >
                <FavoriteButtonIcon isFavorite={isFavorite} />
              </Frap>
            )}
            {isComingFromFav || isComingFromPrev ? (
              <Frap
                className="mb3"
                data-e2e={favOrPrevDataMessage}
                mini
                onClick={handleBackToPrevOrFavClick}
              >
                {formatMessage(messages.backToPreviousOrFavorites, {
                  isComingFromPrevious: isComingFromPrev,
                })}{' '}
              </Frap>
            ) : null}
            <Frap
              data-e2e="add-to-order-button"
              onClick={onAddToCartClickHandler}
            >
              {formatMessage(messages.addToOrder, {
                totalAmount,
              })}
            </Frap>
          </React.Fragment>
        )}
      </div>
      {showTotalAmount && (
        <span aria-live="polite" className="hiddenVisually">
          {formatMessage(messages.totalPriceUpdated, {
            productName: product?.name || '',
            totalAmount,
          })}
        </span>
      )}
    </FrapContainer>
  );
};

export default ProductDetailsFraps;
